import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { FiSearch, FiDownload } from 'react-icons/fi';
import reportesAvanzadosService from '../services/reportesAvanzadosService';
import Loading from '../components/Loading';
import './ReporteActivosPage.css';

const ReporteActivosPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fechaCorte, setFechaCorte] = useState('');
  const [categoria, setCategoria] = useState('');

  const cargar = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (fechaCorte) filters.fecha_corte = fechaCorte;
      if (categoria) filters.categoria = categoria;
      const res = await reportesAvanzadosService.getReporteActivosFijos(filters);
      setData(res.data);
    } catch { toast.error('Error al cargar reporte de activos'); }
    finally { setLoading(false); }
  };

  const formatCurrency = (v) => new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(v || 0);

  const exportarExcel = async () => {
    try {
      const XLSX = (await import('xlsx')).default;
      const activos = data?.activos || [];
      const ws = XLSX.utils.json_to_sheet(activos.map(a => ({
        Codigo: a.codigo, Nombre: a.nombre, Categoria: a.categoria_nombre || a.categoria,
        'Fecha Adquisicion': a.fecha_adquisicion, 'Valor Adquisicion': a.valor_adquisicion,
        'Dep. Acumulada': a.depreciacion_acumulada, 'Valor Libro': a.valor_libro,
        'Vida Util (meses)': a.vida_util_meses, Estado: a.estado,
      })));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Activos Fijos');
      XLSX.writeFile(wb, `reporte_activos_fijos.xlsx`);
    } catch { toast.error('Error al exportar'); }
  };

  const activos = data?.activos || data?.results || [];
  const resumen = data?.resumen_categorias || [];
  const totales = data?.totales || {};

  return (
    <div className="reporte-activos-page">
      <div className="page-header"><h1>Reporte de Activos Fijos</h1></div>

      <div className="selector-bar">
        <div className="form-group">
          <label>Fecha de Corte</label>
          <input type="date" value={fechaCorte} onChange={e => setFechaCorte(e.target.value)} />
        </div>
        <div className="form-group">
          <label>Categoria</label>
          <input value={categoria} onChange={e => setCategoria(e.target.value)} placeholder="Todas" />
        </div>
        <button className="btn-primary" onClick={cargar}><FiSearch /> Consultar</button>
        {data && <button className="btn-export" onClick={exportarExcel}><FiDownload /> Excel</button>}
      </div>

      {loading && <Loading />}

      {!loading && data && (
        <>
          {/* Resumen por categoria */}
          {resumen.length > 0 && (
            <div className="resumen-grid">
              {resumen.map((cat, i) => (
                <div key={i} className="stat-card">
                  <div className="stat-label">{cat.categoria || cat.nombre}</div>
                  <div className="stat-value">{formatCurrency(cat.valor_libro || cat.valor_neto)}</div>
                  <div className="stat-sublabel">{cat.cantidad} activos | Dep. {formatCurrency(cat.depreciacion_acumulada)}</div>
                </div>
              ))}
            </div>
          )}

          {/* Totales cards */}
          <div className="totales-cards">
            <div className="total-card">
              <span>Valor Adquisicion Total</span>
              <strong>{formatCurrency(totales.valor_adquisicion || activos.reduce((s, a) => s + parseFloat(a.valor_adquisicion || 0), 0))}</strong>
            </div>
            <div className="total-card">
              <span>Depreciacion Acumulada</span>
              <strong style={{ color: '#dc2626' }}>{formatCurrency(totales.depreciacion_acumulada || activos.reduce((s, a) => s + parseFloat(a.depreciacion_acumulada || 0), 0))}</strong>
            </div>
            <div className="total-card">
              <span>Valor Libro Total</span>
              <strong style={{ color: '#059669' }}>{formatCurrency(totales.valor_libro || activos.reduce((s, a) => s + parseFloat(a.valor_libro || 0), 0))}</strong>
            </div>
          </div>

          {/* Tabla detalle */}
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Codigo</th>
                  <th>Activo</th>
                  <th>Categoria</th>
                  <th>Adquisicion</th>
                  <th style={{ textAlign: 'right' }}>Valor Adq.</th>
                  <th style={{ textAlign: 'right' }}>Dep. Acum.</th>
                  <th style={{ textAlign: 'right' }}>Valor Libro</th>
                  <th>Vida Util</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {activos.length === 0 ? (
                  <tr><td colSpan="9" style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>Sin activos registrados</td></tr>
                ) : activos.map((a, i) => (
                  <tr key={i}>
                    <td className="mono">{a.codigo}</td>
                    <td><strong>{a.nombre}</strong></td>
                    <td>{a.categoria_nombre || a.categoria}</td>
                    <td>{a.fecha_adquisicion}</td>
                    <td className="mono right">{formatCurrency(a.valor_adquisicion)}</td>
                    <td className="mono right" style={{ color: '#dc2626' }}>{formatCurrency(a.depreciacion_acumulada)}</td>
                    <td className="mono right bold" style={{ color: '#059669' }}>{formatCurrency(a.valor_libro)}</td>
                    <td>{a.vida_util_meses ? `${a.vida_util_meses} meses` : '-'}</td>
                    <td><span className={`badge ${a.estado === 'activo' ? 'badge-green' : a.estado === 'dado_de_baja' ? 'badge-red' : 'badge-gray'}`}>{a.estado_display || a.estado}</span></td>
                  </tr>
                ))}
              </tbody>
              {activos.length > 0 && (
                <tfoot>
                  <tr>
                    <td colSpan="4" style={{ fontWeight: 700 }}>TOTALES ({activos.length} activos)</td>
                    <td className="mono right bold">{formatCurrency(totales.valor_adquisicion || activos.reduce((s, a) => s + parseFloat(a.valor_adquisicion || 0), 0))}</td>
                    <td className="mono right bold" style={{ color: '#dc2626' }}>{formatCurrency(totales.depreciacion_acumulada || activos.reduce((s, a) => s + parseFloat(a.depreciacion_acumulada || 0), 0))}</td>
                    <td className="mono right bold" style={{ color: '#059669' }}>{formatCurrency(totales.valor_libro || activos.reduce((s, a) => s + parseFloat(a.valor_libro || 0), 0))}</td>
                    <td colSpan="2"></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default ReporteActivosPage;
